import { useMemo } from "react";

import { useLocalization } from "@fluent/react";

import { LIVE_LEDGER_CATEGORY_QUERY_KEY } from "../../constants";
import { useLiveLedgerCategories } from "../../hooks/useLiveLedgerCategories";
import { useMultiSelectFilter } from "../../hooks/useMultiSelectFilter";

import MultiSelectFilterPanel from "./MultiSelectFilterPanel";

import type { MultiSelectFilterPanelOption } from "./MultiSelectFilterPanel";

export interface CategoryFilterPanelProps {
  close: () => void;
  onApply: () => void;
}

const CategoryFilterPanel = ({ close, onApply }: CategoryFilterPanelProps) => {
  const { l10n } = useLocalization();
  const {
    consumerCategories,
    businessCategories,
    ownAccountType,
    isMixedAccountAccess,
    validSlugs,
    labelForSlug,
  } = useLiveLedgerCategories();

  const categoryOptions: MultiSelectFilterPanelOption[] = useMemo(() => {
    if (!isMixedAccountAccess) {
      const categories =
        ownAccountType === "business" ? businessCategories : consumerCategories;
      return categories.map((category) => ({
        value: category.slug,
        label: category.label,
      }));
    }
    // The two taxonomies can share a top-level slug (e.g. "income", "other"). Both a business
    // and a consumer row for the same slug would drive the same MultiSelectFilterPanel Set
    // entry, so checking one silently checks (and un-checking either un-checks) the other too.
    // Keep the business row on a collision; drop the consumer duplicate.
    const seenSlugs = new Set<string>();
    const businessOptions = businessCategories
      .filter((category) => {
        if (seenSlugs.has(category.slug)) return false;
        seenSlugs.add(category.slug);
        return true;
      })
      .map((category) => ({
        value: category.slug,
        label: category.label,
        group: l10n.getString("live-ledger-filter-category-group-business"),
      }));
    const consumerOptions = consumerCategories
      .filter((category) => !seenSlugs.has(category.slug))
      .map((category) => ({
        value: category.slug,
        label: category.label,
        group: l10n.getString("live-ledger-filter-category-group-consumer"),
      }));
    return [...businessOptions, ...consumerOptions];
  }, [
    isMixedAccountAccess,
    ownAccountType,
    consumerCategories,
    businessCategories,
    l10n,
  ]);

  const { selectedValues, setSelectedValues } = useMultiSelectFilter({
    queryKey: LIVE_LEDGER_CATEGORY_QUERY_KEY,
    validValues: validSlugs,
    getOptionLabel: labelForSlug,
  });

  return (
    <MultiSelectFilterPanel
      options={categoryOptions}
      selectedValues={selectedValues}
      setSelectedValues={setSelectedValues}
      close={close}
      onApply={onApply}
      searchLabel={l10n.getString("live-ledger-filter-category-search")}
      emptyLabel={l10n.getString("live-ledger-filter-category-empty")}
      applyLabel={l10n.getString("live-ledger-filter-category-apply")}
      removeLabel={l10n.getString("live-ledger-filter-category-remove")}
      testIdPrefix="ledger-category-filter"
    />
  );
};

export default CategoryFilterPanel;
